import { Injectable, Signal, inject } from '@angular/core'

import { Observable, of, tap } from 'rxjs'

import {
  FlxModalComponentType,
  FlxModalConfig,
  FlxModalData,
  FlxModalRef,
  FlxModalService,
} from '@flowx/angular-ui-toolkit'
import { ExpressionTargetType } from '@flowx/designer/modules/ai/conversation/ai-conversation.dictionary'
import { ScriptingLanguage } from '@flowx/designer/modules/core/dictionary/common.types'
import { ProcessDefinition } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { ScriptingModalStore } from '@flowx/designer/modules/script-builder/services/scripting-modal.store'
import { ConfirmationModalComponent } from '@flowx/designer/shared/components/confirmation-modal/confirmation-modal.component'

import { BusinessRuleEditorModalComponent } from '../components/business-rule-editor/business-rule-editor.component'
import { ExpressionEditorModalComponent } from '../components/expression-editor/expression-editor.component'
import { ExpressionCondition } from '../constants/expressions.constants'

@Injectable({
  providedIn: 'root',
})
export class ScriptingModalService {
  #flxModalService = inject(FlxModalService)
  #scriptingModalStore = inject(ScriptingModalStore)

  showBusinessRuleEditor(payload: {
    expression: string
    language: ScriptingLanguage
    supportsGraalVM: boolean
    useGraalVM: boolean
    processVersionId: ProcessDefinition['id']
    processDefinitionName: ProcessDefinition['resourceName']
    withFixWarnings: boolean
    conversationTargetDescriptor?: {
      nodeId?: number
      actionId?: number
      index?: number
    }
    ignoreProgrammaticClose?: boolean
  }): FlxModalRef {
    return this.#showScriptingModal(
      BusinessRuleEditorModalComponent,
      { ...payload },
      { ignoreProgrammaticClose: payload.ignoreProgrammaticClose }
    )
  }

  showExpressionEditor(payload: {
    type: 'expression' | 'computed_value'
    condition?: ExpressionCondition
    expression: string
    processVersionId: ProcessDefinition['id']
    processDefinitionName: ProcessDefinition['resourceName']
    readonly: boolean | Signal<boolean>
    required?: boolean
    conversationTargetDescriptor?: {
      type: ExpressionTargetType
      index?: number
      nodeId: number
      propName?: string
      templateId: number
    }
    showAiActions: boolean
    saveButtonLabel?: string
    title?: string
  }): FlxModalRef {
    return this.#showScriptingModal(ExpressionEditorModalComponent, { ...payload })
  }

  onModalClose(hasUnsavedChanges: boolean, callback?: () => void): Observable<boolean> {
    if (!hasUnsavedChanges) {
      callback?.()
      this.#scriptingModalStore.activeModal.set(null)
      return of(true)
    }

    const confirmationModal = this.#flxModalService.show(
      ConfirmationModalComponent,
      {
        closeOnBackdropClick: false,
        backdropClass: 'overlay-backdrop',
      },
      {
        title: `Changes Unsaved`,
        message: `You have unsaved changes. Are you sure you want to exit page?`,
        btnOkText: `Discard Changes`,
        modalClass: 'pending-changes',
      }
    )

    return confirmationModal.data$.pipe(
      tap(({ confirm }) => {
        if (confirm) {
          this.#scriptingModalStore.activeModal.set(null)
          callback?.()
        }
      })
    )
  }

  #showScriptingModal(
    component: FlxModalComponentType,
    data?: FlxModalData,
    config?: FlxModalConfig
  ): FlxModalRef {
    if (this.#scriptingModalStore.activeModal()) {
      return
    }

    this.#scriptingModalStore.activeModal.set(
      this.#flxModalService.show(
        component,
        {
          ...config,
          panelClass: 'scripting-modal',
        },
        data ?? {}
      )
    )

    return this.#scriptingModalStore.activeModal()
  }
}
